import type { CropPoint, CropRect } from './types';

export enum CropAnchorAlignment {
  Near,
  Center,
  Far,
}

export const createCropRect = (x: number, y: number, width: number, height: number): CropRect => ({
  x,
  y,
  width,
  height,
});

export const cloneCropRect = (rect: CropRect): CropRect => ({
  x: rect.x,
  y: rect.y,
  width: rect.width,
  height: rect.height,
});

export const normalizeCropRect = (rect: CropRect | null | undefined): CropRect | null => {
  if (!rect) {
    return null;
  }

  return {
    x: Math.min(rect.x, rect.x + rect.width),
    y: Math.min(rect.y, rect.y + rect.height),
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  };
};

export const getCropRectAnchorPoint = (
  rect: CropRect,
  horizontalAlignment: CropAnchorAlignment,
  verticalAlignment: CropAnchorAlignment,
): CropPoint => {
  const point: CropPoint = {
    x: rect.x,
    y: rect.y,
  };

  switch (horizontalAlignment) {
    case CropAnchorAlignment.Center:
      point.x = rect.x + rect.width / 2;
      break;
    case CropAnchorAlignment.Far:
      point.x = rect.x + rect.width;
      break;
  }

  switch (verticalAlignment) {
    case CropAnchorAlignment.Center:
      point.y = rect.y + rect.height / 2;
      break;
    case CropAnchorAlignment.Far:
      point.y = rect.y + rect.height;
      break;
  }

  return point;
};

export const scaleCropRect = (
  rect: CropRect,
  center: CropPoint,
  scaleX: number,
  scaleY = scaleX,
): CropRect => {
  const x1 = center.x + (rect.x - center.x) * scaleX;
  const x2 = center.x + (rect.x + rect.width - center.x) * scaleX;
  const y1 = center.y + (rect.y - center.y) * scaleY;
  const y2 = center.y + (rect.y + rect.height - center.y) * scaleY;

  return {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
  };
};

export const getCropHandlePoints = (rect: CropRect): ReadonlyArray<CropPoint> => [
  { x: rect.x + rect.width / 2, y: rect.y },
  { x: rect.x + rect.width, y: rect.y },
  { x: rect.x + rect.width, y: rect.y + rect.height / 2 },
  { x: rect.x + rect.width, y: rect.y + rect.height },
  { x: rect.x + rect.width / 2, y: rect.y + rect.height },
  { x: rect.x, y: rect.y + rect.height },
  { x: rect.x, y: rect.y + rect.height / 2 },
  { x: rect.x, y: rect.y },
];

export const clampCropRectToBounds = (
  rect: CropRect | null | undefined,
  bounds: { width: number; height: number },
): CropRect | null => {
  const normalized = normalizeCropRect(rect);
  if (!normalized) {
    return null;
  }

  const maxX = Math.max(bounds.width - 1, 0);
  const maxY = Math.max(bounds.height - 1, 0);
  const x = Math.min(Math.max(normalized.x, 0), maxX);
  const y = Math.min(Math.max(normalized.y, 0), maxY);

  return {
    x,
    y,
    width: Math.min(normalized.width, Math.max(bounds.width - x, 0)),
    height: Math.min(normalized.height, Math.max(bounds.height - y, 0)),
  };
};
